import React, { useEffect, useState, useCallback } from 'react';
import { pb, blogService, getFileUrl } from '../services/pocketbase';
import { STATIC_POSTS } from '../services/staticData';
import { Post, Comment } from '../types';
import { updateSEO } from '../utils/seo';
import Button from '../components/Button';
import Logo from '../components/Logo';

interface PostDetailProps {
  slug: string;
}

const PostDetail: React.FC<PostDetailProps> = ({ slug }) => {
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');

  const initPostData = useCallback(async (data: Post) => {
    updateSEO({ title: data.title, description: data.excerpt });
    try {
      if (data.id !== 'static-epstein') {
        const count = await blogService.getLikeCount(data.id);
        setLikeCount(count);
        const hasLiked = await blogService.hasUserLiked(data.id);
        setLiked(hasLiked);
        const result = await blogService.getComments(data.id, 1);
        setComments(result.items as any);
      }
    } catch (e) {
      console.warn("Post detail data initialization partial failure.");
    }
  }, []);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setError(false);
      try {
        const data = await blogService.getPostBySlug(slug);
        setPost(data as any);
        await initPostData(data as any);
      } catch (err) {
        if (STATIC_POSTS[slug]) {
          const staticData = STATIC_POSTS[slug] as Post;
          setPost(staticData);
          await initPostData(staticData);
        } else {
          setError(true);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug, initPostData]);

  const handleToggleLike = async () => {
    if (!pb.authStore.isValid) {
      window.location.hash = '#/login';
      return;
    }
    if (!post || post.id === 'static-epstein') return;
    try {
      const result = await blogService.toggleLike(post.id);
      setLiked(result);
      setLikeCount(prev => result ? prev + 1 : prev - 1);
    } catch (err) {
      console.error(err);
    }
  };

  const handlePostComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !post || post.id === 'static-epstein') return;
    try {
      await blogService.createComment(post.id, newComment);
      setNewComment('');
      const result = await blogService.getComments(post.id, 1);
      setComments(result.items as any);
    } catch (err) {
      alert("Failed to post comment.");
    }
  };

  if (loading) return (
    <div className="fixed inset-0 bg-white flex flex-col items-center justify-center z-50">
      <Logo size={60} className="text-black animate-bounce" />
      <p className="text-[10px] font-black uppercase tracking-[0.4em] text-gray-400">Loading Technical Draft...</p>
    </div>
  );

  if (error || !post) return (
    <div className="min-h-screen flex flex-col items-center justify-center p-20 text-center">
      <h2 className="text-2xl font-black uppercase tracking-tighter mb-4">Draft Sequence Missing</h2>
      <Button variant="outline" onClick={() => window.location.hash = '#/blog'}>Return to Archive</Button>
    </div>
  );

  const displayImage = post.image ? getFileUrl(post, post.image) : 
    (slug === 'shadow-of-influence' ? "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=2070&auto=format&fit=crop" : "");

  return (
    <article className="bg-white min-h-screen pb-24 animate-in fade-in duration-700">
      <header className="max-w-4xl mx-auto px-6 pt-16 md:pt-24 mb-16">
        <h1 className="text-5xl md:text-8xl font-black tracking-tighter leading-[0.85] mb-10 uppercase text-black">
          {post.title}
        </h1>
        <p className="text-xl md:text-3xl text-gray-400 font-medium mb-12 italic leading-relaxed">
          {post.excerpt}
        </p>
        {displayImage && (
          <div className="w-full aspect-video overflow-hidden border-2 border-black shadow-[20px_20px_0px_0px_rgba(249,115,22,1)]">
            <img src={displayImage} alt={post.title} className="w-full h-full object-cover grayscale" />
          </div>
        )}
      </header>
      <div className="max-w-3xl mx-auto px-6">
        <div 
          className="prose prose-xl prose-orange max-w-none text-gray-900 leading-relaxed mb-24 font-serif"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
        <section className="pt-20 border-t-4 border-black">
          <h3 className="text-4xl font-black uppercase tracking-tighter text-black mb-12">Discussions</h3>
          {post.id === 'static-epstein' ? (
             <div className="bg-orange-50 p-12 border-2 border-black text-center mb-16 uppercase font-black text-xs tracking-widest text-black">
              Discussion for this featured archival piece is currently locked.
            </div>
          ) : pb.authStore.isValid ? (
            <form onSubmit={handlePostComment} className="mb-16">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Participate in analysis..."
                className="w-full px-8 py-6 border-2 border-black focus:border-orange-500 outline-none min-h-[160px] mb-6 font-bold text-xl"
              />
              <Button variant="primary">Submit Feedback</Button>
            </form>
          ) : (
            <div className="text-center py-10 bg-gray-50 border-2 border-black mb-16">
               <p className="font-black text-xs uppercase">Login required to join discussion</p>
            </div>
          )}
          <div className="space-y-12">
            {comments.map((comment) => (
              <div key={comment.id} className="pb-12 border-b-2 border-gray-100">
                <h4 className="text-lg font-black uppercase">{comment.expand?.user?.name || 'Anonymous'}</h4>
                <p className="text-gray-700">{comment.content}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </article>
  );
};

export default PostDetail;